import { useCallback, useRef, useState } from "react";
import ImageLightbox, {
  ImageZoomButton,
} from "../components/ImageLightbox/ImageLightbox";
import s from "./TourGallerySlider.module.scss";

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M19 12H5" />
    <path d="M11 6l-6 6 6 6" />
  </svg>
);

const buildMobileCards = (images) => {
  if (images.length === 0) return [];

  return images.reduce((groups, image, index) => {
    const groupIndex = Math.floor(index / 4);
    groups[groupIndex] = groups[groupIndex] || [];
    groups[groupIndex].push(image);
    return groups;
  }, []);
};

const buildSliderImages = (images) => {
  if (images.length === 0) return [];

  const minimumCount = 8;
  const base = Array.from(
    { length: Math.max(minimumCount, images.length) },
    (_, index) => images[index % images.length],
  );

  return [...base, ...base];
};

const TourGallerySlider = ({
  images = [],
  isVertical = false,
  isCompact = false,
}) => {
  const galleryImages = Array.isArray(images) ? images.filter(Boolean) : [];
  const mobileCards = buildMobileCards(galleryImages);
  const sliderImages = buildSliderImages(galleryImages);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const sliderViewportRef = useRef(null);
  const sliderTrackRef = useRef(null);
  const sliderOffsetRef = useRef(0);
  const closeLightbox = useCallback(() => setEnlargedImage(null), []);

  const updateSliderPosition = useCallback(() => {
    const track = sliderTrackRef.current;
    if (!track) return;

    const halfWidth = track.scrollWidth / 2;
    if (halfWidth <= 0) return;

    sliderOffsetRef.current =
      ((sliderOffsetRef.current % halfWidth) + halfWidth) % halfWidth;
    track.style.transform = `translate3d(${-sliderOffsetRef.current}px, 0, 0)`;
  }, []);

  const scrollSlider = useCallback(
    (direction) => {
      const viewport = sliderViewportRef.current;
      if (!viewport || !sliderTrackRef.current) return;

      const scrollAmount = Math.min(520, viewport.clientWidth * 0.8);
      sliderOffsetRef.current += scrollAmount * direction;
      updateSliderPosition();
    },
    [updateSliderPosition],
  );

  if (galleryImages.length === 0) return null;

  return (
    <section
      className={`${s.root} ${isVertical ? s.vertical : ""} ${
        isCompact ? s.compact : ""
      }`}
    >
      <div className={s.mobileGallery}>
        {mobileCards.map((group, cardIndex) => (
          <div className={s.mobileCard} key={cardIndex}>
            <div className={s.imageFrame}>
              <img
                className={s.mainImage}
                src={group[0]}
                alt={`Tour gallery ${cardIndex + 1}`}
              />
              <ImageZoomButton
                className={s.compactZoom}
                onClick={() => setEnlargedImage(group[0])}
                ariaLabel={`Tour gallery ${cardIndex + 1}を大きく見る`}
              />
            </div>

            <div className={s.thumbs}>
              {group.slice(1, 4).map((image, index) => (
                <div className={s.thumbFrame} key={`${cardIndex}-${index}`}>
                  <img
                    src={image}
                    alt={`Tour gallery thumbnail ${index + 1}`}
                  />
                  <ImageZoomButton
                    className={s.compactZoom}
                    onClick={() => setEnlargedImage(image)}
                    ariaLabel={`Tour gallery thumbnail ${index + 1}を大きく見る`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className={s.slider}>
        <div className={s.sliderControls} aria-label="ギャラリー操作">
          <button
            type="button"
            onClick={() => scrollSlider(-1)}
            aria-label="前の写真を見る"
          >
            <ArrowLeftIcon />
          </button>
          <button
            type="button"
            onClick={() => scrollSlider(1)}
            aria-label="次の写真を見る"
          >
            <ArrowRightIcon />
          </button>
        </div>

        <div className={s.sliderViewport} ref={sliderViewportRef}>
          <div className={s.track} ref={sliderTrackRef}>
            {sliderImages.map((image, index) => (
              <div className={s.trackItem} key={`${image}-${index}`}>
                <img src={image} alt="Tour gallery" />
                <ImageZoomButton
                  className={s.compactZoom}
                  onClick={() => setEnlargedImage(image)}
                  ariaLabel="Tour galleryを大きく見る"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <ImageLightbox
        image={enlargedImage}
        alt="Tour gallery"
        onClose={closeLightbox}
      />
    </section>
  );
};

export default TourGallerySlider;
